import { ACCESS_ROLE } from '../../lib/access';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { StatBox } from '../../components/ui/StatBox.jsx';
import { RAYON_CHANTIER, distLisible, distM } from '../../lib/geo';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';

export function GpsPage({
  onNav,
  soc,
  employes,
  setEmployes,
  chantiers,
  estMixte,
  univers,
  setUnivers,
}) {
  // Au magasin, pas de chantier : le GPS montre où sont les livreurs, par la
  // position qu'ils partagent depuis leur espace.
  const mag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  const todayISO = new Date().toISOString().slice(0, 10);
  const pos = [
    { x: 32, y: 40 },
    { x: 64, y: 30 },
    { x: 48, y: 62 },
    { x: 74, y: 58 },
  ];
  const data = employes.map((e, i) => {
    const ptsToday = chantiers.flatMap((c) =>
      (c.pointages || [])
        .filter((p) => p.empId === e.id && p.date === todayISO)
        .map((p) => ({ ...p, chNom: c.nom, ch: c })),
    );
    const lastPt = ptsToday[ptsToday.length - 1];
    const live = !!(e.gps && e.gps.actif && typeof e.gps.lat === 'number');
    const liveCh = chantiers.find((c) => c.id === (e.gps && e.gps.chId));
    const liveDist =
      live && liveCh && liveCh.gps && typeof liveCh.gps.lat === 'number'
        ? distM(e.gps.lat, e.gps.lng, liveCh.gps.lat, liveCh.gps.lng)
        : null;
    const ptDist =
      lastPt &&
      typeof lastPt.latE === 'number' &&
      lastPt.ch &&
      lastPt.ch.gps &&
      typeof lastPt.ch.gps.lat === 'number'
        ? distM(lastPt.latE, lastPt.lngE, lastPt.ch.gps.lat, lastPt.ch.gps.lng)
        : null;
    let chNom = null,
      onSite = null,
      dist = null,
      source = null,
      lat,
      lng;
    if (live && liveDist != null) {
      chNom = liveCh.nom;
      dist = liveDist;
      onSite = liveDist <= RAYON_CHANTIER;
      source = 'live';
      lat = e.gps.lat;
      lng = e.gps.lng;
    } else if (lastPt && lastPt.onSiteEntree != null) {
      chNom = lastPt.chNom;
      onSite = lastPt.onSiteEntree;
      dist = ptDist;
      source = 'pointage';
      lat = lastPt.latE;
      lng = lastPt.lngE;
    } else if (live) {
      chNom = liveCh ? liveCh.nom : null;
      source = 'live-nogeo';
      lat = e.gps.lat;
      lng = e.gps.lng;
    } else if (lastPt) {
      chNom = lastPt.chNom;
      source = 'pointage-nogeo';
    }
    const pointe = !!lastPt;
    return {
      e,
      lastPt,
      pointe,
      live,
      onSite,
      dist,
      chNom,
      source,
      lat,
      lng,
      p: pos[i % pos.length],
    };
  });
  const gpsTeamOn = employes.length > 0 && employes.every((e) => e.gps && e.gps.actif);
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="📍 GPS équipe"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {mag ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <StatBox
            label="Position partagée"
            value={String(data.filter((d) => typeof d.lat === 'number').length)}
            color={C.green}
            icon="📍"
          />
          <StatBox
            label="Position non partagée"
            value={String(data.filter((d) => typeof d.lat !== 'number').length)}
            color={C.muted}
            icon="⚪"
          />
        </div>
      ) : (
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}
        >
          <StatBox
            label="Sur le chantier"
            value={String(data.filter((d) => d.onSite === true).length)}
            color={C.green}
            icon="✅"
          />
          <StatBox
            label="Hors zone"
            value={String(data.filter((d) => d.onSite === false).length)}
            color={C.red}
            icon="⚠"
          />
          <StatBox
            label="Pas pointé"
            value={String(data.filter((d) => !d.pointe && !d.live).length)}
            color={C.muted}
            icon="⚪"
          />
        </div>
      )}
      {/* Le GPS de toute l'équipe ne s'allume que depuis le bureau. */}
      {ACCESS_ROLE !== 'ouvrier' &&
        (() => {
          const tous = gpsTeamOn;
          return (
            <Card style={{ marginBottom: 12 }}>
              <div
                style={{
                  padding: '12px 13px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1, marginRight: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800 }}>📍 Suivi GPS de l'équipe</div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {tous ? "Activé pour toute l'équipe" : "Un seul bouton pour toute l'équipe"}
                  </div>
                </div>
                <Btn
                  label={tous ? 'Désactiver le GPS' : 'Activer le GPS'}
                  sm
                  color={tous ? C.red : C.teal}
                  onClick={() =>
                    setEmployes &&
                    setEmployes((p) =>
                      p.map((x) => ({ ...x, gps: { ...(x.gps || {}), actif: !tous } })),
                    )
                  }
                />
              </div>
            </Card>
          );
        })()}
      {(() => {
        const wPts = data.filter((d) => typeof d.lat === 'number');
        const cPts = chantiers.filter((c) => c.gps && typeof c.gps.lat === 'number');
        const all = [
          ...wPts.map((d) => ({ lat: d.lat, lng: d.lng })),
          ...cPts.map((c) => ({ lat: c.gps.lat, lng: c.gps.lng })),
        ];
        const b = all.length
          ? {
              a: Math.min(...all.map((p) => p.lat)),
              A: Math.max(...all.map((p) => p.lat)),
              o: Math.min(...all.map((p) => p.lng)),
              O: Math.max(...all.map((p) => p.lng)),
            }
          : null;
        const xy = (lat, lng) => {
          if (!b) return null;
          const dLat = b.A - b.a || 0.002,
            dLng = b.O - b.o || 0.002;
          return { x: 12 + 76 * ((lng - b.o) / dLng), y: 84 - 72 * ((lat - b.a) / dLat) };
        };
        return (
          <Card style={{ marginBottom: 12 }}>
            <Bar
              icon="🗺"
              title="Carte de l'équipe"
              color={C.navy}
              sub={b ? 'Positions réelles (pointage / GPS)' : "Aucune position pour l'instant"}
            />
            <div
              style={{
                position: 'relative',
                height: 210,
                background: 'linear-gradient(135deg,#E8EEF6,#DCE6F2)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(rgba(22,32,53,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(22,32,53,.06) 1px,transparent 1px)',
                  backgroundSize: '22px 22px',
                }}
              />
              {cPts.map((c) => {
                const q = xy(c.gps.lat, c.gps.lng);
                if (!q) return null;
                return (
                  <div
                    key={'c' + c.id}
                    style={{
                      position: 'absolute',
                      left: q.x + '%',
                      top: q.y + '%',
                      transform: 'translate(-50%,-50%)',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 18 }}>🏗️</div>
                    <div
                      style={{
                        fontSize: 6.5,
                        fontWeight: 700,
                        color: C.navy,
                        background: 'rgba(255,255,255,.8)',
                        borderRadius: 4,
                        padding: '0 3px',
                        maxWidth: 64,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.nom}
                    </div>
                  </div>
                );
              })}
              {data.map((d, i) => {
                const q = typeof d.lat === 'number' ? xy(d.lat, d.lng) : b ? null : d.p;
                if (!q) return null;
                return (
                  <div
                    key={d.e.id}
                    style={{
                      position: 'absolute',
                      left: q.x + '%',
                      top: q.y + '%',
                      transform: 'translate(-50%,-50%)',
                      textAlign: 'center',
                      zIndex: 2,
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: d.e.col,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 6px rgba(0,0,0,.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 12,
                      }}
                    >
                      {d.e.nom.charAt(0)}
                    </div>
                    <div
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background:
                          d.onSite === true ? C.green : d.onSite === false ? C.red : C.muted,
                        border: '1.5px solid #fff',
                        position: 'absolute',
                        top: -2,
                        right: -2,
                      }}
                    />
                  </div>
                );
              })}
              {!b && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontSize: 8,
                    color: C.grey,
                  }}
                >
                  {mag
                    ? 'Les positions s’affichent quand les employés partagent leur GPS'
                    : "Les positions s'affichent quand les ouvriers pointent (ou partagent leur GPS)"}
                </div>
              )}
            </div>
          </Card>
        );
      })()}
      {data.map((d) => (
        <Card key={d.e.id} style={{ marginBottom: 8 }}>
          <div style={{ padding: '11px 13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: d.e.col,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 900,
                  }}
                >
                  {d.e.nom.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700 }}>{d.e.nom}</div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {mag
                      ? typeof d.lat === 'number'
                        ? '📍 ' +
                          tr('Position partagée') +
                          (d.e.gps?.at ? ' · ' + tr('mise à jour à') + ' ' + d.e.gps.at : '')
                        : tr('Position non partagée')
                      : d.chNom
                        ? '🏗 ' + d.chNom
                        : 'Aucun chantier'}
                    {d.lastPt
                      ? ' · ' +
                        d.lastPt.entree +
                        (d.lastPt.sortie ? '→' + d.lastPt.sortie : '→ en cours')
                      : ''}
                  </div>
                </div>
              </div>
              <Pill
                label={
                  mag
                    ? typeof d.lat === 'number'
                      ? '📍 Partagée'
                      : '⚪ Non partagée'
                    : d.onSite === true
                      ? '✅ Sur le chantier'
                      : d.onSite === false
                        ? '⚠ Hors zone'
                        : d.pointe
                          ? '🟢 Pointé'
                          : '⚪ Pas pointé'
                }
                color={
                  mag
                    ? typeof d.lat === 'number'
                      ? C.green
                      : C.muted
                    : d.onSite === true
                      ? C.green
                      : d.onSite === false
                        ? C.red
                        : d.pointe
                          ? C.teal
                          : C.muted
                }
                size={8}
              />
            </div>
            {d.onSite != null && (
              <div
                style={{
                  marginTop: 9,
                  borderRadius: 8,
                  padding: '8px 10px',
                  background: d.onSite ? C.greenL : C.redL,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 9.5, fontWeight: 800, color: d.onSite ? C.green : C.red }}>
                  {d.onSite ? '✅ Sur le chantier' : '⚠ Hors de la zone de travail'}
                  {d.chNom ? ' · ' + d.chNom : ''}
                </span>
                {d.dist != null && (
                  <span style={{ fontSize: 8.5, color: d.onSite ? C.green : C.red }}>
                    {d.onSite ? 'à ' + distLisible(d.dist) : distLisible(d.dist) + ' du site'}
                  </span>
                )}
              </div>
            )}
            {d.onSite == null && d.pointe && (
              <div style={{ marginTop: 9, fontSize: 8.5, color: C.amber, fontWeight: 700 }}>
                Pointé, mais position non vérifiée — définissez la position GPS du chantier (fiche
                chantier → Résumé).
              </div>
            )}
            {d.onSite == null && !d.pointe && d.live && (
              <div style={{ marginTop: 9, fontSize: 8.5, color: C.muted }}>
                GPS activé — en attente de la position de l'ouvrier.
              </div>
            )}
            {typeof d.lat === 'number' && (
              <a
                href={'https://maps.google.com/?q=' + d.lat + ',' + d.lng}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: C.tealL,
                    borderRadius: 8,
                    padding: '8px 10px',
                    marginTop: 9,
                  }}
                >
                  <span style={{ fontSize: 9, color: C.teal, fontWeight: 700 }}>
                    🗺 Voir sur la carte ({d.lat.toFixed(4)}, {d.lng.toFixed(4)})
                  </span>
                  <span style={{ fontSize: 12, color: C.teal }}>›</span>
                </div>
              </a>
            )}
          </div>
        </Card>
      ))}
      <div
        style={{ fontSize: 8, color: C.muted, textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}
      >
        {mag
          ? 'L’employé partage sa position depuis son espace (Pointer mes heures). Pratique pour suivre les livraisons.'
          : "La présence vient du pointage de l'ouvrier (connexion + GPS). Définissez la position de chaque chantier (fiche chantier → Résumé) pour vérifier la zone."}
      </div>
    </Shell>
  );
}
